import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

const VendorsDropdown = ({
  onClose,
  triggerRef,
  onMouseEnter,
  onMouseLeave,
}) => {
  const { t } = useTranslation("navbar");
  const navigate = useNavigate();
  const [activeVendor, setActiveVendor] = useState(null);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const vendors = [
    {
      name: t("vendors_list.shg.name"),
      items: [
        {
          name: t("vendors_list.shg.items.monitoring"),
          slug: "conveyor-belt-operation-shg-yc2i5i",
        },
      ],
    },
    {
      name: t("vendors_list.censor.name"),
      items: [
        {
          name: t("vendors_list.censor.items.mill_operation"),
          slug: "high-performance-mill-operation-obbuft",
        },
        {
          name: t("vendors_list.censor.items.pipeline_monitoring"),
          slug: "efficient-operation-of-slurry-pipelines-6c48im",
        },
      ],
    },
    {
      name: t("vendors_list.intellithink.name"),
      items: [
        {
          name: t("vendors_list.intellithink.items.equipment_monitoring"),
          slug: "ensuring-reliability-of-rotating-equipment-rfc8m8",
        },
      ],
    },
    {
      name: t("vendors_list.skygeo.name"),
      items: [
        {
          name: t("vendors_list.skygeo.items.satellite_monitoring"),
          slug: "satellite-monitoring-a1myjm",
        },
      ],
    },
    {
      name: t("vendors_list.ms4m.name"),
      items: [
        {
          name: t("vendors_list.ms4m.items.fatigue_monitoring"),
          slug: "sistema-monitoringa-ustalosti-ms4m-zsdg6e",
        },
        {
          name: t("vendors_list.ms4m.items.underground_dispatch"),
          slug: "real-time-location-system-rtls-1i3ih1",
        },
        {
          name: t("vendors_list.ms4m.items.ore_blending"),
          slug: "ore-blending-optimization-groundhog-hvfkgp",
        },
        {
          name: t("vendors_list.ms4m.items.precision_drilling"),
          slug: "high-precision-drilling-cfflv4",
        },
        {
          name: t("vendors_list.ms4m.items.precision_excavation"),
          slug: "high-precision-excavationloading-z59foq",
        },
        {
          name: t("vendors_list.ms4m.items.open_pit_optimization"),
          slug: "open-pit-operations-optimization-nnfcca",
        },
      ],
    },
    {
      name: t("vendors_list.detect.name"),
      items: [
        {
          name: t("vendors_list.detect.items.ai_security"),
          slug: "bezopasnost-predpriiatii-s-ispolzovaniem-ii-amuve9",
        },
      ],
    },
    {
      name: t("vendors_list.flagman.name"),
      items: [
        {
          name: t("vendors_list.flagman.items.collision_prevention"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.korra.name"),
      items: [
        {
          name: t("vendors_list.korra.items.core_analysis"),
          slug: "optimization-of-core-analysis-xrsgjl",
        },
        {
          name: t("vendors_list.korra.items.ore_composition"),
          slug: "optimization-of-ore-body-modeling-mdwxjn",
        },
      ],
    },
    {
      name: t("vendors_list.prediktera.name"),
      items: [
        {
          name: t("vendors_list.prediktera.items.core_analysis"),
          slug: "optimization-of-core-analysis-xrsgjl",
        },
        {
          name: t("vendors_list.prediktera.items.ore_composition"),
          slug: "optimization-of-ore-body-modeling-mdwxjn",
        },
      ],
    },
    {
      name: t("vendors_list.lithologiq.name"),
      items: [
        {
          name: t("vendors_list.lithologiq.items.core_analysis"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.stratum.name"),
      items: [
        {
          name: t("vendors_list.stratum.items.ore_body_modeling"),
          slug: "optimizaciia-modelirovaniia-rudnyx-tel-g5qfrg",
        },
      ],
    },
    {
      name: t("vendors_list.beyond_mining.name"),
      items: [
        {
          name: t("vendors_list.beyond_mining.items.ore_blending"),
          slug: "ore-blending-beyond-mining-pufaxy",
        },
        {
          name: t("vendors_list.beyond_mining.items.smelting_optimization"),
          slug: "smelting-fwoxja",
        },
        {
          name: t("vendors_list.beyond_mining.items.flotation_optimization"),
          slug: "floatation-optimization-xcxbc8",
        },
      ],
    },
    {
      name: t("vendors_list.wipware.name"),
      items: [
        {
          name: t("vendors_list.wipware.items.ore_fragmentation"),
          slug: "ore-fragmentation-zpc66n",
        },
      ],
    },
    {
      name: t("vendors_list.walz_scale.name"),
      items: [
        {
          name: t("vendors_list.walz_scale.items.conveyor_material"),
          slug: "conveyor-material-calculation-gsfzbh",
        },
        {
          name: t("vendors_list.walz_scale.items.railcar_material"),
          slug: "railcar-material-calculation-0fwsrr",
        },
      ],
    },
    {
      name: t("vendors_list.beltspy.name"),
      items: [
        {
          name: t("vendors_list.beltspy.items.conveyor_monitoring"),
          slug: "conveyor-belt-operation-beltspy-ht3j7g",
        },
      ],
    },
    {
      name: t("vendors_list.cbguard.name"),
      items: [
        {
          name: t("vendors_list.cbguard.items.conveyor_monitoring"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.fluid_intelligence.name"),
      items: [
        {
          name: t("vendors_list.fluid_intelligence.items.oil_quality"),
          slug: "ensuring-reliability-of-gears-hydraulics-and-motors-bcneia",
        },
      ],
    },
    {
      name: t("vendors_list.aisimpro.name"),
      items: [
        {
          name: t("vendors_list.aisimpro.items.gold_processing"),
          slug: "gold-processing-optimization-j46seq",
        },
      ],
    },
    {
      name: t("vendors_list.jumine.name"),
      items: [
        {
          name: t("vendors_list.jumine.items.flotation_control"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.hawk.name"),
      items: [
        {
          name: t("vendors_list.hawk.items.pipeline_monitoring"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.steinert.name"),
      items: [
        {
          name: t("vendors_list.steinert.items.magnetic_separators"),
          slug: null,
        },
      ],
    },
    {
      name: t("vendors_list.rajant.name"),
      items: [
        {
          name: t("vendors_list.rajant.items.underground_networks"),
          slug: null,
        },
      ],
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, triggerRef]);

  const handleServiceClick = (slug) => {
    if (slug) {
      navigate(`/solution/${slug}`);
      onClose();
    }
  };

  const handleVendorClick = (vendor) => {
    if (isMobile) {
      setActiveVendor((prev) => (prev?.name === vendor.name ? null : vendor));
    }
  };

  const handleVendorHover = (vendor) => {
    if (!isMobile) {
      setActiveVendor(vendor);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute left-0 z-50 bg-transparent"
      style={{ top: "100%" }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`${isMobile ? "w-full" : "flex"} items-start`}>
        {/* Основное меню */}
        <div
          className={`${
            isMobile ? "w-full" : "w-[300px]"
          } border-r border-white/10 bg-[rgb(42,42,42)]`}
        >
          <ul className="py-2 max-h-[60dvh] overflow-y-auto">
            {vendors.map((vendor) => (
              <li
                key={vendor.name}
                className="relative"
                onMouseEnter={() => handleVendorHover(vendor)}
              >
                <button
                  type="button"
                  onClick={() => handleVendorClick(vendor)}
                  className={`w-full px-4 py-3 text-left transition-all duration-200 ${
                    activeVendor?.name === vendor.name
                      ? "text-[#42fbfe] bg-white/10"
                      : "text-white hover:text-[#42fbfe] hover:bg-white/5"
                  }`}
                >
                  <span className="flex items-center justify-between">
                    {vendor.name}
                    {vendor.items.length > 0 && (
                      <svg
                        className={`w-4 h-4 ml-2 transition-transform ${
                          activeVendor?.name === vendor.name
                            ? "text-[#42fbfe]"
                            : ""
                        } ${
                          isMobile && activeVendor?.name === vendor.name
                            ? "rotate-90"
                            : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    )}
                  </span>
                </button>

                {/* Мобильное подменю */}
                {isMobile && activeVendor?.name === vendor.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-[rgb(64,64,64)] max-h-[calc(50dvh-48px)] overflow-y-auto"
                  >
                    <ul className="py-2 px-4 space-y-2">
                      {vendor.items.map((item) => (
                        <li
                          key={item.slug || item.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleServiceClick(item.slug);
                          }}
                          className="py-2"
                        >
                          <span className={`flex items-center justify-between transition-colors text-white/80 ${
                            item.slug 
                              ? "hover:text-[#42fbfe] cursor-pointer" 
                              : "cursor-default"
                          }`}>
                            <span>{item.name}</span>
                            {item.slug && (
                              <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2 opacity-50 group-hover:opacity-100 transition-opacity shrink-0" />
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Десктопное подменю */}
        {!isMobile && (
          <AnimatePresence>
            {activeVendor && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="w-[350px] p-4 max-h-[calc(100dvh-102px)] overflow-y-auto bg-[rgb(42,42,42)]"
              >
                <h3 className="text-[#42fbfe] font-semibold mb-4">
                  {activeVendor.name}
                </h3>
                <ul className="space-y-2">
                  {activeVendor.items.map((item) => (
                    <li
                      key={item.slug || item.name}
                      className="group"
                      onClick={() => handleServiceClick(item.slug)}
                    >
                      <button 
                        className={`w-full text-left px-3 py-2 text-white/80 transition-all duration-200 ${
                          item.slug 
                            ? "hover:text-[#42fbfe] hover:bg-white/5 rounded cursor-pointer" 
                            : "cursor-default"
                        }`}
                      >
                        <span className="flex items-center justify-between">
                          <span className="block" title={item.name}>
                            {item.name}
                          </span>
                          {item.slug && (
                            <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-2 opacity-30 group-hover:opacity-100 transition-opacity shrink-0" />
                          )}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default VendorsDropdown;
